import React, { useMemo } from "react";
import { EmptyStateErro } from "../../components/EmptyState";
import PainelTabela from "../../components/PainelTabela";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { atribuicaoDe } from "../../components/ProjetistaExecutivo/regras";
import { useDados } from "../../context/DadosContext";
import useProjetistasExecutivo from "../../hooks/useProjetistasExecutivo";
import { fmtDiasEmFila } from "../../utils/calculos";
import {
  STATUS_FORA_DE_ORDEM,
  entradaNaRevisao,
  estaEmRevisao,
  foraDeOrdem,
  limiteDaRevisao,
} from "../../utils/fluxoExecutivo";
import { F } from "../../utils/gerencialFields";
import { buildDeadlineStatus, compareDatesAsc } from "../../utils/status";

const COLUNAS = [
  { key: "cliente", label: "Cliente", noWrap: true },
  { key: "ambiente", label: "Ambiente" },
  // Quem fez o executivo (backend, somente leitura aqui).
  { key: "projetista", label: "Projetista", labelCurto: "projetista", width: "150px" },
  { key: "entregaContrato", label: "Entrega Prevista", labelCurto: "entrega", tipo: "data", width: "110px" },
  { key: "revisaoLimite", label: "Revisão Limite", labelCurto: "limite", tipo: "data", width: "110px" },
  { key: "valor", label: "Valor", labelCurto: "valor", tipo: "moeda", width: "110px", requerPermissao: "preco:view" },
  { key: "diasFila", label: "Dias em fila", labelCurto: "fila", width: "90px" },
  { key: "status", label: "Status", width: "110px" },
];

// Ordem antiga: a revisão vinha depois da aprovação e não tinha limite.
const SEM_LIMITE = { status: "Sem limite", statusVariant: "neutral" };
const FORA_DE_ORDEM = { status: STATUS_FORA_DE_ORDEM, statusVariant: "warning" };

function statusDaLinha(row, revisaoLimite) {
  if (foraDeOrdem(row)) return FORA_DE_ORDEM;
  return revisaoLimite ? buildDeadlineStatus(revisaoLimite) : SEM_LIMITE;
}

export default function Revisao() {
  const { dados, erro, initialLoading } = useDados();
  const { atribuicoes } = useProjetistasExecutivo();

  // Memoizado (e antes dos early-returns — Rules of Hooks): sem isso, cada
  // re-render recriava `linhas` (filter+map+sort de 400+ itens) e a nova
  // referência invalidava toda a cadeia de memos do PainelTabela.
  // A fila depende da ordem do executivo de cada linha (utils/fluxoExecutivo):
  // na nova, entra quem terminou o executivo; na antiga, quem foi aprovado.
  const linhas = useMemo(
    () =>
      dados
        .filter(estaEmRevisao)
        .map((row) => {
          const revisaoLimite = limiteDaRevisao(row);
          return {
            cliente: row[F.NOME_DO_CLIENTE],
            rowPlanilha: row,
            ambiente: row[F.AMBIENTE],
            projetista: atribuicaoDe(row, atribuicoes)?.projetista || "",
            entregaContrato: row[F.ENTREGA_PREVISTA],
            revisaoLimite,
            valor: row[F.VALOR_DO_AMBIENTE],
            diasFila: fmtDiasEmFila(entradaNaRevisao(row)),
            ...statusDaLinha(row, revisaoLimite),
          };
        })
        .sort((left, right) => compareDatesAsc(left.revisaoLimite, right.revisaoLimite)),
    [dados, atribuicoes],
  );

  const totalizadores = useMemo(
    () => [
      { label: "Total de Ambientes", valor: linhas.length },
      {
        label: "Em Atraso",
        valor: linhas.filter((linha) => linha.statusVariant === "danger").length,
      },
      {
        label: "Fora de ordem",
        valor: linhas.filter((linha) => linha.status === STATUS_FORA_DE_ORDEM).length,
      },
    ],
    [linhas],
  );

  if (initialLoading) {
    return <PageLoader scope="revisao" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  return (
    <PainelTabela
      titulo="Revisão"
      // Id novo junto com as colunas novas (limite e status): as larguras
      // salvas no layout antigo empurravam a tabela para fora da tela.
      exportPainelId="revisao-executivo"
      colunas={COLUNAS}
      linhas={linhas}
      totalizadores={totalizadores}
      densidade="compact"
      mostrarPaginacao={false}
      persistirNoBackend
    />
  );
}
